import { useState, useEffect, useRef } from 'react';

/**
 * Multi-select dropdown filter with chip display for selected values.
 *
 * Props:
 *   label      — string shown as the filter label
 *   values     — array of currently selected values (empty = ALL)
 *   onChange   — called with new array when selection changes
 *   options    — array of available option strings (raw values)
 *   chipColor  — optional fn(value) => hex color string for chips
 *   labelMap   — optional { rawValue: 'Display Label' } for human-readable names
 */
export function MultiFilterSelect({ label, values, onChange, options, chipColor, labelMap }) {
  const displayLabel = (v) => labelMap?.[v] ?? v;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function toggle(opt) {
    onChange(values.includes(opt) ? values.filter(v => v !== opt) : [...values, opt]);
  }

  const triggerText = values.length === 0 ? 'ALL'
    : values.length === 1 ? displayLabel(values[0])
    : `${values.length} selected`;

  return (
    <div ref={ref} style={{ display: 'flex', flexDirection: 'column', gap: 5, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className="filter-label">{label}</span>
        <button
          className="filter-select"
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 8, minWidth: 120, cursor: 'pointer',
          }}
        >
          <span>{triggerText}</span>
          <span style={{ fontSize: 9, opacity: 0.5, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
        </button>
      </div>

      {/* Selected chips */}
      {values.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {values.map(v => {
            const color = chipColor?.(v) || '#6366f1';
            return (
              <span key={v} style={{
                fontSize: 10, fontWeight: 600,
                background: color + '18',
                border: `1px solid ${color}35`,
                color,
                borderRadius: 100, padding: '2px 6px 2px 8px',
                display: 'inline-flex', alignItems: 'center', gap: 4, lineHeight: 1.4,
              }}>
                {displayLabel(v)}
                <span
                  onClick={() => toggle(v)}
                  style={{ cursor: 'pointer', opacity: 0.65, fontSize: 12, lineHeight: 1, fontWeight: 400 }}
                >×</span>
              </span>
            );
          })}
        </div>
      )}

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0,
          background: 'var(--bg-card)', border: '1px solid var(--border-mid)',
          borderRadius: 8, padding: '4px 0', zIndex: 300,
          minWidth: 190, boxShadow: '0 6px 20px rgba(0,0,0,0.28)',
        }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 9,
            padding: '7px 12px', cursor: 'pointer', fontSize: 12,
            color: 'var(--text-primary)',
          }}>
            <input
              type="checkbox"
              checked={values.length === 0}
              onChange={() => onChange([])}
              style={{ cursor: 'pointer', accentColor: 'var(--teal)' }}
            />
            ALL
          </label>
          <div style={{ height: 1, background: 'var(--border-mid)', margin: '2px 8px' }} />
          {options.map(opt => (
            <label key={opt} style={{
              display: 'flex', alignItems: 'center', gap: 9,
              padding: '7px 12px', cursor: 'pointer', fontSize: 12,
              color: 'var(--text-primary)',
            }}>
              <input
                type="checkbox"
                checked={values.includes(opt)}
                onChange={() => toggle(opt)}
                style={{ cursor: 'pointer', accentColor: 'var(--teal)' }}
              />
              {displayLabel(opt)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
